/**
 * AuthContext — central source of auth state.
 *
 * Per the data-layer handoff §6, two auth surfaces must coexist:
 *
 *   1. `sessionStorage["portal_jwt"]` — primary, read by the chatbot widget
 *      from outside React.
 *   2. React context — for components that prefer the idiomatic surface.
 *
 * This file owns the React-side surface. It mirrors `sessionStorage`;
 * `sessionStorage` is the writer, the context is the reader.
 *
 * Why not Zustand? The portal app is a ~3-page flow with a single auth
 * concern. Pulling Zustand from `@tensaw/runtime` would force a transitive
 * dependency on the staff-app's store wiring (clinicId, lastActivityAt,
 * etc.) that doesn't apply here. A 30-line React context is the right size.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import {
  clearAuth,
  getValidToken,
  readPatientId,
  readTenantId,
} from '../api/storage';

export interface AuthState {
  /** Valid (non-expired) JWT, or null. */
  jwt: string | null;
  patientId: number | null;
  tenantId: string | null;
  /** True if the current state would survive a refresh (i.e. not expired). */
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  /** Notify the context that storage has changed (call after storeAuth/clearAuth). */
  refresh: () => void;
  /** Clear storage and refresh state. Doesn't navigate; caller decides. */
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  jwt: null,
  patientId: null,
  tenantId: null,
  isAuthenticated: false,
  refresh: () => { /* noop default */ },
  signOut: () => { /* noop default */ },
});

function readState(): AuthState {
  const jwt = getValidToken();
  return {
    jwt,
    patientId: readPatientId(),
    tenantId: readTenantId(),
    isAuthenticated: jwt !== null,
  };
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AuthState>(() => readState());

  const refresh = useCallback(() => {
    setState(readState());
  }, []);

  const signOut = useCallback(() => {
    clearAuth();
    setState(readState());
  }, []);

  // Cross-tab sync: the chatbot widget may live in a different tab/iframe
  // and clear the token there. Listen for storage events so we react.
  // (sessionStorage doesn't fire `storage` events for same-tab writes,
  // which is fine — same-tab callers go through `refresh` explicitly.)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key.startsWith('portal_')) {
        refresh();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('storage', onStorage); };
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, refresh, signOut }),
    [state, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
